<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-250" @keyup.escape="handleEscape">
      <!-- Backdrop -->
      <Transition name="fade" appear>
        <div
          v-if="showBackdrop"
          class="absolute inset-0 bg-black/20 tablet-h:bg-gray-800/35"
          @click="handleBackdropClick"
        />
      </Transition>

      <!-- Desktop: Modal (also used on mobile for modal presentation) -->
      <div
        v-if="!isMobile || forceModal"
        class="absolute inset-0 flex overflow-y-auto p-6 tablet-h:p-0 tablet-h:py-5"
        :style="{ height: `${viewportHeight}px` }"
        @click.self="handleOutsideClick"
      >
        <Transition name="modal" appear>
          <div
            v-if="showBackdrop && dialogs.length"
            class="z-130 m-auto flex w-full justify-center"
            @click.self="handleOutsideClick"
          >
            <ModalWrapper
              :hide-close-button="!!topDialog?.hideCloseButton"
              :class="topDialog?.modal?.class"
            >
              <template v-for="(dialog, index) in dialogs" :key="dialog.id">
                <div
                  :class="{
                    'opacity-0 absolute! top-[-999px]':
                      index !== dialogs.length - 1,
                  }"
                >
                  <DialogSlot
                    :id="dialog.id"
                    :index="index"
                    :modal="dialog.presentation === 'modal'"
                    :hide-close-button="!!dialog.hideCloseButton"
                  >
                    <component
                      :is="resolveComponent(dialog.name)"
                      v-bind="dialog.props"
                    />
                  </DialogSlot>
                </div>
              </template>
            </ModalWrapper>
          </div>
        </Transition>
      </div>

      <!-- Mobile: Bottom Sheet (stacked horizontally, only for adaptive presentation) -->
      <Transition v-else name="slideup" appear>
        <div
          v-if="showMobileSheet"
          class="transition-transform duration-300 absolute bottom-0 w-full flex"
          :style="mobileTranslateStyle"
        >
          <BottomSheetWrapper
            v-for="(dialog, index) in dialogs"
            :key="dialog.id"
            :slideup-height="dialog.slideup?.height ?? 'content'"
            :disable-dismiss="!!dialog.preventOutsideClick"
            :class="['min-w-full', dialog.slideup?.class]"
          >
            <DialogSlot
              :id="dialog.id"
              :index="index"
              :modal="dialog.presentation === 'modal'"
              :hide-close-button="!!dialog.hideCloseButton"
            >
              <component
                :is="resolveComponent(dialog.name)"
                v-bind="dialog.props"
              />
            </DialogSlot>
          </BottomSheetWrapper>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watchEffect,
  onBeforeUnmount,
  type Component,
} from 'vue'
import { useDevice } from '@/shared/composables/useDevice'
import { useDialog } from './useDialog'
import { resolveDialog } from './registry'
import ModalWrapper from './ModalWrapper.vue'
import BottomSheetWrapper from './BottomSheetWrapper.vue'
import DialogSlot from './DialogSlot.vue'
import type { Dialogs } from './types'

const { dialogs, isOpen, closeAll } = useDialog()
const { isMobile, viewportHeight } = useDevice()

const showBackdrop = computed(() => isOpen.value && dialogs.value.length > 0)

watchEffect(() => {
  document.body.style.overflow = isOpen.value ? 'hidden' : ''
})

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})

const visibleDialogs = computed(() =>
  dialogs.value.filter((d) => d.visible !== false)
)
const topDialog = computed(() => dialogs.value.at(-1))
const canOutsideDismissTopDialog = computed(
  () => !topDialog.value?.preventOutsideClick
)
const canEscapeDismissTopDialog = computed(
  () => !topDialog.value?.hideCloseButton
)

// When stacked (>1): always show container, horizontal translate handles animation
// When single (1): only show if that slideup is visible
const showMobileSheet = computed(() => {
  if (!isOpen.value || !dialogs.value.length) return false
  return dialogs.value.length > 1
    ? true
    : dialogs.value.length === visibleDialogs.value.length
})

const mobileTranslateStyle = computed(() => {
  const translateX = Math.max(
    window.innerWidth * (visibleDialogs.value.length - 1),
    0
  )
  return { translate: `-${translateX}px` }
})

// Track whether we're in modal mode — stays true during close animation
const wasModal = ref(false)
const hasModalDialog = computed(() =>
  dialogs.value.some((d) => d.presentation === 'modal')
)

watchEffect(() => {
  if (dialogs.value.length) {
    wasModal.value = hasModalDialog.value
  }
})
const forceModal = computed(() =>
  dialogs.value.length ? hasModalDialog.value : wasModal.value
)

const resolveComponent = (name: Dialogs): Component | null => {
  return resolveDialog(name) ?? null
}

const handleEscape = () => {
  if (canEscapeDismissTopDialog.value) {
    closeAll()
  }
}
const handleBackdropClick = () => {
  if (canOutsideDismissTopDialog.value) {
    closeAll()
  }
}

const handleOutsideClick = (event: MouseEvent) => {
  if (!canOutsideDismissTopDialog.value) return

  if (event.target === event.currentTarget) {
    closeAll()
  }
}
</script>
